import React, { useState } from 'react';
import './PartnershipForm.css';
import LocationSelector from '../../components/common/Forms/LocationSelector';
import { partnershipAPI } from '../../services/api';

function PartnershipForm() {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    country: '',
    state: '',
    city: '',
    organizationType: 'Non Governmental Organization (NGO)',
    otherOrganizationType: '',
    description: '',
    partnershipGoals: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  // Add constants for maximum word counts
  const MAX_DESCRIPTION_WORDS = 500;
  const MAX_GOALS_WORDS = 300;

  // Helper function to count words
  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const finalOrganizationType = formData.organizationType === 'Other' 
        ? formData.otherOrganizationType 
        : formData.organizationType;

      const response = await partnershipAPI.submit({
        ...formData,
        organizationType: finalOrganizationType
      });

      setSubmitStatus({
        type: 'success',
        message: 'Your form has been submitted successfully. We will contact you soon.'
      });
      
      // Reset form
      setFormData({
        organizationName: '',
        contactPerson: '',
        email: '',
        phone: '',
        website: '',
        country: '',
        state: '',
        city: '',
        organizationType: 'Non Governmental Organization (NGO)',
        otherOrganizationType: '',
        description: '',
        partnershipGoals: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'There was an error submitting your form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="partnership-form-container">
      <div className="partnership-form-content">
        <h1>Partnership Form</h1>
        <p className="intro-text">
          Thank you for reaching out to us. Kindly complete the form below, and our team will get in touch with you shortly to explore potential collaboration opportunities.
        </p>

        <form onSubmit={handleSubmit} className="partnership-form">
          <div className="form-group">
            <label htmlFor="organizationName">Organization/Institution Name *</label>
            <input
              type="text"
              id="organizationName"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactPerson">Contact Person *</label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://"
            />
          </div>

          <div className="form-group">
            <label>Location *</label>
            <LocationSelector
              countryCode={formData.country}
              stateCode={formData.state}
              city={formData.city}
              onCountryChange={(code) => {
                setFormData(prev => ({
                  ...prev,
                  country: code,
                  state: '',
                  city: ''
                }));
              }}
              onStateChange={(code) => {
                setFormData(prev => ({
                  ...prev,
                  state: code,
                  city: ''
                }));
              }}
              onCityChange={(cityName) => {
                setFormData(prev => ({
                  ...prev,
                  city: cityName
                }));
              }}
              required={true}
            />
          </div>

          <div className="form-group">
            <label htmlFor="organizationType">Organization Type *</label>
            <select
              id="organizationType"
              name="organizationType"
              value={formData.organizationType}
              onChange={handleChange}
              required
            >
              <option value="Non Governmental Organization (NGO)">Non Governmental Organization (NGO)</option>
              <option value="Government">Government Organization</option>
              <option value="Educational">Educational Institution</option>
              <option value="Corporate">Corporate Organization</option>
              <option value="Other">Other</option>
            </select>
            {formData.organizationType === 'Other' && (
              <input
                type="text"
                id="otherOrganizationType"
                name="otherOrganizationType"
                value={formData.otherOrganizationType}
                onChange={handleChange}
                placeholder="Please specify organization type"
                required
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Organization Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              placeholder="Please describe your organization and its main activities"
            />
            <div className="word-count">
              {countWords(formData.description)} / {MAX_DESCRIPTION_WORDS} words
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="partnershipGoals">Partnership Goals *</label>
            <textarea
              id="partnershipGoals"
              name="partnershipGoals"
              value={formData.partnershipGoals}
              onChange={handleChange}
              required
              rows="4"
              placeholder="What do you hope to achieve through this partnership?"
            />
            <div className="word-count">
              {countWords(formData.partnershipGoals)} / {MAX_GOALS_WORDS} words
            </div>
          </div>

          {submitStatus.message && (
            <div className={`status-message ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Form'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PartnershipForm; 